import React, { useEffect, useRef } from 'react';

import styles from './ResizeObserver.module.scss';

interface IResizeObserverProps {
  onResize: (width: number, height: number) => void;
}

/**
 * 부모 요소의 크기가 변하면 `onResize`를 호출한다.
 *
 * 부모 요소는 `position: static`이 아닌 값을 가져야 하며, 인라인 요소의 크기는 측정할 수 없다.
 *
 * 오버헤드가 존재하므로 주의해야 한다.
 *
 * @see http://www.backalleycoder.com/2013/03/18/cross-browser-event-based-element-resize-detection/
 */
const ResizeObserver: React.FC<IResizeObserverProps> = ({ onResize }) => {
  const el = useRef<HTMLObjectElement>(null);
  const raf = useRef<number>();

  const handleResizeOnAnimFrame = (event: UIEvent): void => {
    if (raf.current !== undefined) {
      cancelAnimationFrame(raf.current);
    }

    const win = event.target as Window;

    raf.current = requestAnimationFrame(() => {
      onResize(win.innerWidth, win.innerHeight);
      raf.current = undefined;
    });
  };

  useEffect(() => {
    const observingWindow = el.current?.contentDocument?.defaultView;
    observingWindow?.addEventListener('resize', handleResizeOnAnimFrame);

    return () => {
      observingWindow?.removeEventListener('resize', handleResizeOnAnimFrame);
    };
  }, []);

  return <object ref={el} className={styles.observer} data="about:blank" type="text/html" />;
};

export default ResizeObserver;
